import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FaPlay, FaSearch, FaHistory, FaUser, FaCheckCircle, FaClock, FaCalendarAlt, FaUsers, FaVideo } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";

const POSTS_PER_PAGE = 10;

const RaiseHistory = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // Calculate total pages
  const totalPages = Math.ceil(posts.length / POSTS_PER_PAGE);

  // Get posts for current page
  const paginatedPosts = [...posts]
    .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
    .slice((currentPage - 1) * POSTS_PER_PAGE, currentPage * POSTS_PER_PAGE);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  useEffect(() => {
    const fetchPostHistory = async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/get/history/ryv/post`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        console.log("Post history response:", response);
        const data = response.data.data || response.data;
        const voices = Array.isArray(data) ? data : [data];
        setPosts(voices);
      } catch (error) {
        if (error.response && error.response.status === 404) {
          setPosts([]);
        } else {
          toast.error("Failed to load post history");
        }
      } finally {
        setLoading(false);
      }
    };
    fetchPostHistory();
  }, []);

  // Filter posts based on search term
  const filteredPosts = posts.filter(post =>
    post.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    post._id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 gap-4">
        <h1 className="text-2xl font-bold text-gray-800">
          Raise Your Voice History
        </h1>
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative">
            <input
              type="text"
              placeholder="Search posts..."
              className="pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          </div>
          <Link
            to="/raise/runningvoice"
            className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600"
          >
            Running Voices
          </Link>
        </div>
      </div>

      {/* Stats Card */}
      <div className="mb-8">
        <div className="bg-purple-500 p-6 rounded-xl shadow-lg text-white">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm opacity-80">Completed Work</p>
              <p className="text-2xl font-bold">{filteredPosts.length}</p>
              <div className="flex gap-4 mt-2 text-xs">
                <span className="bg-white/20 px-2 py-1 rounded">
                  Total Completed: {posts.reduce((sum, post) => sum + (post.totalCompleted || 0), 0)}
                </span>
                <span className="bg-white/20 px-2 py-1 rounded">
                  Reporters: {posts.reduce((sum, post) => sum + (post.totalReporters || 0), 0)}
                </span>
              </div>
            </div>
            <FaHistory className="w-10 h-10 opacity-80" />
          </div>
        </div>
      </div>

      {/* Posts Cards/Table */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
        </div>
      ) : filteredPosts.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center">
          <FaHistory className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-xl font-semibold text-gray-700">
            No Completed Work Found
          </h3>
          <p className="text-gray-500">
            {searchTerm ? "No posts match your search criteria." : "You don't have any completed work at the moment."}
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
            {paginatedPosts.map((post) => (
              <div
                key={post._id}
                className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
              >
                {/* Post Header */}
                <div className="p-4 border-b border-gray-100">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-800 mb-2">
                        RYV ID: <span className="text-purple-600 font-mono">{post._id}</span>
                      </h3>

                      {/* Post Details */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                        <div className="flex items-center gap-2 text-gray-600">
                          <FaUser className="w-4 h-4 text-green-500 flex-shrink-0" />
                          <span className="font-medium">Description:</span>
                          <span className="text-gray-800 truncate">{post.description || "N/A"}</span>
                        </div>

                        <div className="flex items-center gap-2 text-gray-600">
                          <FaUsers className="w-4 h-4 text-indigo-500 flex-shrink-0" />
                          <span className="font-medium">Completed:</span>
                          <div className="flex gap-2">
                            <span className="text-gray-800 font-semibold bg-green-100 px-2 py-1 rounded-full text-xs">
                              Work: {post.totalCompleted || 0}
                            </span>
                            <span className="text-gray-800 font-semibold bg-blue-100 px-2 py-1 rounded-full text-xs">
                              Reporters: {post.totalReporters || 0}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Reporters List */}
                {post.reporters && post.reporters.length > 0 ? (
                  <div className="p-4">
                    <div className="mb-4">
                      <h4 className="text-md font-semibold text-gray-700 mb-3 flex items-center gap-2">
                        <FaUsers className="w-4 h-4 text-purple-500" />
                        Completed Work ({post.reporters.length})
                      </h4>
                    </div>

                    {/* Desktop Table View */}
                    <div className="hidden lg:block overflow-x-auto">
                      <div className="min-w-full">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reporter</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Platform</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Video</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Completed Date</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {post.reporters.map((reporter, idx) => (
                              <tr key={reporter._id || idx} className="hover:bg-gray-50 transition-colors">
                                <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                  <div className="flex items-center gap-2">
                                    <FaUser className="w-3 h-3 text-gray-400" />
                                    <div>
                                      <div className="font-semibold">{reporter.reporterId?.name || "N/A"}</div>
                                      <div className="text-xs text-gray-500">{reporter.reporterId?.iinsafId || "N/A"}</div>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900">
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    {reporter.platform || "N/A"}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900">
                                  {reporter.videoLink ? (
                                    <a
                                      href={reporter.videoLink}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-1 px-3 py-1 bg-green-500 text-white rounded-md hover:bg-green-600 text-sm transition-colors"
                                    >
                                      <FaPlay className="w-3 h-3" /> Watch
                                    </a>
                                  ) : (
                                    <span className="text-gray-400">N/A</span>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900">
                                  <div className="flex items-center gap-1">
                                    <FaClock className="w-3 h-3 text-gray-400" />
                                    {reporter.duration || "N/A"}
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900">
                                  <div className="flex items-center gap-1">
                                    <FaCalendarAlt className="w-3 h-3 text-gray-400" />
                                    {(() => {
                                      const dateField = reporter.completedAt || reporter.submittedAt;
                                      if (!dateField) return "N/A";
                                      const d = new Date(dateField);
                                      if (isNaN(d.getTime())) return "Invalid Date";
                                      const date = d.toLocaleDateString("en-GB");
                                      const time = d.toLocaleTimeString("en-GB", { hour12: true });
                                      return (
                                        <div className="text-xs">
                                          <div>{date}</div>
                                          <div className="text-gray-500">{time}</div>
                                        </div>
                                      );
                                    })()}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Mobile Card View */}
                    <div className="lg:hidden space-y-3">
                      {post.reporters.map((reporter, idx) => (
                        <div key={reporter._id || idx} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                          <div className="space-y-3">
                            {/* Reporter Info */}
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <FaUser className="w-4 h-4 text-purple-500" />
                                <div>
                                  <div className="font-medium text-gray-900">{reporter.reporterId?.name || "N/A"}</div>
                                  <div className="text-xs text-gray-500">{reporter.reporterId?.iinsafId || "N/A"}</div>
                                </div>
                              </div>
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                <FaCheckCircle className="w-3 h-3 mr-1" /> Completed
                              </span>
                            </div>

                            {/* Platform */}
                            <div className="text-sm text-gray-700">
                              <span className="font-medium">Platform:</span> {reporter.platform || "N/A"}
                            </div>

                            {/* Duration */}
                            <div className="flex items-center gap-2 text-sm text-gray-700">
                              <FaClock className="w-3 h-3 text-gray-400" />
                              <span className="font-medium">Duration:</span>
                              <span>{reporter.duration || "N/A"}</span>
                            </div>

                            {/* Completed Date */}
                            <div className="flex items-center gap-2 text-sm text-gray-700">
                              <FaCalendarAlt className="w-3 h-3 text-gray-400" />
                              <span className="font-medium">Completed:</span>
                              <span>
                                {(() => {
                                  const dateField = reporter.completedAt || reporter.submittedAt;
                                  if (!dateField) return "N/A";
                                  const d = new Date(dateField);
                                  if (isNaN(d.getTime())) return "Invalid Date";
                                  const date = d.toLocaleDateString("en-GB");
                                  const time = d.toLocaleTimeString("en-GB", { hour12: true });
                                  return `${date} ${time}`;
                                })()}
                              </span>
                            </div>

                            {/* Video Link */}
                            {reporter.videoLink && (
                              <div>
                                <a
                                  href={reporter.videoLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-2 px-3 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 text-sm transition-colors"
                                >
                                  <FaVideo className="w-4 h-4" /> Watch Video
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="p-6 text-center">
                    <FaUsers className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <h3 className="text-xl font-semibold text-gray-700">
                      No Completed Work
                    </h3>
                    <p className="text-gray-500">
                      No reporters have completed work for this voice post yet.
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex justify-center mt-6">
              <nav className="inline-flex flex-wrap items-center gap-2 md:space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`px-3 py-1 rounded border border-purple-400 text-purple-800 bg-white hover:bg-purple-100 transition-colors ${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                  aria-label="Previous Page"
                >
                  Previous
                </button>
                {/* Page Numbers */}
                <div className="flex flex-wrap items-center justify-center gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`w-8 h-8 flex items-center justify-center rounded-full border border-purple-400 text-purple-800 bg-white hover:bg-purple-100 transition-colors text-sm font-semibold ${currentPage === page ? 'bg-purple-300 text-purple-900 border-purple-500' : ''}`}
                      aria-current={currentPage === page ? 'page' : undefined}
                    >
                      {page}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`px-3 py-1 rounded border border-purple-400 text-purple-800 bg-white hover:bg-purple-100 transition-colors ${currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''}`}
                  aria-label="Next Page"
                >
                  Next
                </button>
              </nav>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default RaiseHistory;
